import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
  CreditAccount, CreateCreditAccountDto,
  createAccount, updateAccount,
} from "../../api/credit";
import api from "../../api/axios";

interface Props {
  account: CreditAccount | null; // null = создание нового
  onClose: () => void;
  onSaved: () => void;
}

const inp: React.CSSProperties = {
  width: "100%", padding: "8px 10px", borderRadius: 7,
  border: "1px solid #374151", background: "#1a2030",
  color: "#f9fafb", fontSize: 13, outline: "none", boxSizing: "border-box",
};

export default function CreditAccountModal({ account, onClose, onSaved }: Props) {
  const [clients, setClients] = useState<any[]>([]);
  const [pipelines, setPipelines] = useState<any[]>([]);
  const [stages, setStages] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<CreateCreditAccountDto>({
    clientId: account?.clientId ?? 0,
    limitAmount: account?.limitAmount ?? 0,
    paymentTermDays: account?.paymentTermDays ?? 30,
    pipelineId: account?.pipelineId ?? undefined,
    stageId: account?.stageId ?? undefined,
    allowedProductIds: account?.allowedProductIds ?? null,
    allowedCategoryIds: account?.allowedCategoryIds ?? null,
    isActive: account?.isActive ?? true,
  });

  const [restrictMode, setRestrictMode] = useState<"none" | "products" | "categories">(
    account?.allowedProductIds?.length ? "products"
    : account?.allowedCategoryIds?.length ? "categories"
    : "none"
  );

  useEffect(() => {
    Promise.all([
      api.get("/clients").then(r => r.data),
      api.get("/deals/pipelines").then(r => r.data),
      api.get("/products").then(r => r.data),
      api.get("/products/categories").then(r => r.data),
    ]).then(([c, p, pr, cat]) => {
      // /clients возвращает массив напрямую
      setClients(Array.isArray(c) ? c : []);
      setPipelines(Array.isArray(p) ? p : []);
      setProducts(Array.isArray(pr) ? pr.filter((x: any) => x.type !== "service") : []);
      setCategories(Array.isArray(cat) ? cat : []);
    });
  }, []);

  useEffect(() => {
    if (!form.pipelineId) { setStages([]); return; }
    api.get(`/deals/pipelines/${form.pipelineId}/stages`)
      .then(r => setStages(Array.isArray(r.data) ? r.data : []));
  }, [form.pipelineId]);

  const set = (patch: Partial<CreateCreditAccountDto>) =>
    setForm(f => ({ ...f, ...patch }));

  const toggleProduct = (id: number) => {
    const cur = form.allowedProductIds ?? [];
    set({ allowedProductIds: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] });
  };

  const toggleCategory = (id: number) => {
    const cur = form.allowedCategoryIds ?? [];
    set({ allowedCategoryIds: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] });
  };

  const handleSave = async () => {
    if (!form.clientId) return alert("Выберите клиента");
    if (!form.limitAmount) return alert("Укажите лимит");

    const dto: CreateCreditAccountDto = {
      ...form,
      allowedProductIds: restrictMode === "products" ? (form.allowedProductIds?.length ? form.allowedProductIds : null) : null,
      allowedCategoryIds: restrictMode === "categories" ? (form.allowedCategoryIds?.length ? form.allowedCategoryIds : null) : null,
    };

    setSaving(true);
    try {
      if (account) {
        await updateAccount(account.id, dto);
      } else {
        await createAccount(dto);
      }
      onSaved();
      onClose();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="modal-window" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div className="modal-title">
            {account ? "Редактировать кредитный аккаунт" : "Новый кредитный аккаунт"}
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Клиент */}
          {!account && (
            <div>
              <label style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 4 }}>Клиент</label>
              <select style={inp} value={form.clientId || ""} onChange={e => set({ clientId: +e.target.value })}>
                <option value="">— выберите —</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.firstName} {c.lastName} {c.phone ? `· ${c.phone}` : ""}</option>
                ))}
              </select>
            </div>
          )}

          {/* Лимит и срок */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 4 }}>Кредитный лимит (₸)</label>
              <input style={inp} type="number" min={0} value={form.limitAmount}
                onChange={e => set({ limitAmount: +e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 4 }}>Срок оплаты (дней)</label>
              <input style={inp} type="number" min={1} value={form.paymentTermDays}
                onChange={e => set({ paymentTermDays: +e.target.value })} />
            </div>
          </div>

          {/* Воронка */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 4 }}>Воронка для сделок</label>
              <select style={inp} value={form.pipelineId ?? ""} onChange={e => set({ pipelineId: +e.target.value || undefined, stageId: undefined })}>
                <option value="">— не выбрана —</option>
                {pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 4 }}>Стадия</label>
              <select style={inp} value={form.stageId ?? ""} onChange={e => set({ stageId: +e.target.value || undefined })} disabled={!stages.length}>
                <option value="">— не выбрана —</option>
                {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          {/* Ограничение по товарам */}
          <div>
            <label style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 6 }}>Доступные товары</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {(["none", "products", "categories"] as const).map(m => (
                <button key={m} type="button"
                  onClick={() => setRestrictMode(m)}
                  style={{
                    padding: "5px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer",
                    border: restrictMode === m ? "1.5px solid #6366f1" : "1.5px solid #374151",
                    background: restrictMode === m ? "#312e81" : "#1a2030",
                    color: restrictMode === m ? "#a5b4fc" : "#9ca3af",
                  }}>
                  {m === "none" ? "Все товары" : m === "products" ? "Конкретные товары" : "По категории"}
                </button>
              ))}
            </div>

            {restrictMode === "products" && (
              <div style={{ maxHeight: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2, border: "1px solid #374151", borderRadius: 8, padding: "8px 10px", background: "#111827" }}>
                {products.length === 0 && <div style={{ color: "#6b7280", fontSize: 12 }}>Загрузка товаров...</div>}
                {products.map(p => (
                  <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#f9fafb", cursor: "pointer", padding: "4px 0", userSelect: "none" }}>
                    <input type="checkbox"
                      checked={(form.allowedProductIds ?? []).includes(p.id)}
                      onChange={() => toggleProduct(p.id)}
                      style={{ width: 15, height: 15, cursor: "pointer", flexShrink: 0 }} />
                    <span>{p.nameRu}</span>
                    {p.sku && <span style={{ color: "#6b7280", fontSize: 11 }}>· {p.sku}</span>}
                  </label>
                ))}
              </div>
            )}

            {restrictMode === "categories" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 2, border: "1px solid #374151", borderRadius: 8, padding: "8px 10px", background: "#111827" }}>
                {categories.length === 0 && <div style={{ color: "#6b7280", fontSize: 12 }}>Загрузка категорий...</div>}
                {categories.map(c => (
                  <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#f9fafb", cursor: "pointer", padding: "4px 0", userSelect: "none" }}>
                    <input type="checkbox"
                      checked={(form.allowedCategoryIds ?? []).includes(c.id)}
                      onChange={() => toggleCategory(c.id)}
                      style={{ width: 15, height: 15, cursor: "pointer", flexShrink: 0 }} />
                    {c.name}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Активен */}
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#f9fafb", cursor: "pointer" }}>
            <input type="checkbox" checked={form.isActive}
              onChange={e => set({ isActive: e.target.checked })} />
            Аккаунт активен
          </label>
        </div>

        <div className="modal-footer">
          <button className="secondary-btn" onClick={onClose}>Отмена</button>
          <button className="primary-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Сохранение..." : "Сохранить"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
